import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: object | object[];
  alternates?: { hrefLang: string; href: string }[];
}

export function SEOHead({ 
  title, 
  description, 
  keywords,
  canonicalUrl,
  ogImage = 'https://elefa.tel/og-image.png',
  structuredData,
  alternates,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', 'website', true);
    setMeta('og:image', ogImage, true);
    if (canonicalUrl) setMeta('og:url', canonicalUrl, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    // hreflang alternates: one set per destination, replaced on every route change
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
    (alternates ?? []).forEach((alt) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = alt.hrefLang;
      link.href = alt.href;
      document.head.appendChild(link);
    });

    // Structured data - support single object or array of objects
    const existingScripts = document.querySelectorAll('script[data-seo-ld]');
    existingScripts.forEach(s => s.remove());

    if (structuredData) {
      const schemas = Array.isArray(structuredData) ? structuredData : [structuredData];
      schemas.forEach((schema, i) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-ld', `${i}`);
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      const ldScripts = document.querySelectorAll('script[data-seo-ld]');
      ldScripts.forEach(s => s.remove());
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogImage, structuredData, alternates]);

  return null;
}
